import { ListInvoiceFilter } from './types/ListInvoiceFilter';
import { ListInvoicePagination } from './types/ListInvoicePagination';
import { ListInvoiceStatus } from './types/ListInvoiceStatus';
import { ListInvoiceSorting } from './types/ListInvoiceSorting';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';
import { InvoiceDataService } from '../invoice-data.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SortDirection, StorageKey, StorageService, UserPagination } from '@lib/core';
import { ListInvoice } from './types/ListInvoice';
import { Params } from '@angular/router';
import { UserPaginationService } from '@lib/user';
import { IimDateRangeValue } from '@iim-angular/components';
import { IStandaloneInvoicePreviewDto } from './types/IStandaloneInvoicePreview';

export const INVOICE_SORT_DIRECTION = 'sortDirection';

interface IListInvoicesState {
    invoices: ListInvoice[];
    pagination: ListInvoicePagination;
    filter: ListInvoiceFilter;
    sortDirection: SortDirection;
    sortBy: ListInvoiceSorting;
    isLoading: boolean;
}

const initialState: IListInvoicesState = {
    invoices: [],
    pagination: new ListInvoicePagination(),
    filter: new ListInvoiceFilter(),
    sortDirection: SortDirection.DESC,
    sortBy: ListInvoiceSorting.CREATE_DATE,
    isLoading: false,
};

export const ListInvoiceStore = signalStore(
    withState(initialState),
    withMethods(
        (
            store,
            dataService = inject(InvoiceDataService),
            storageService = inject(StorageService),
            paginationService = inject(UserPaginationService),
        ) => ({
            /**
             * Updates the filter criteria for the list of invoices and persists the changes in storage.
             *
             * @param {ListInvoiceFilter} filter - The new filter object used to update the invoice list.
             * @return {void} This method does not return any value.
             */
            setFilter(filter: ListInvoiceFilter): void {
                patchState(store, { filter });
                storageService.setItem(StorageKey.INVOICE_LIST_FILTER, JSON.stringify(filter.toDto()));
            },

            /**
             * Sets the document date range filter and returns a new filter instance.
             *
             * @param {IimDateRangeValue} value - The date range value to set
             * @return {void} This method does not return any value.
             */
            setDocumentDateRange(value: IimDateRangeValue): void {
                const newFilter = store.filter().withDocumentDateRange(value);
                this.setFilter(newFilter);
            },

            /**
             * Resets the document date filter and returns a new filter instance.
             *
             * @return {void} This method does not return any value.
             */
            resetDocumentDateFilter(): void {
                const newFilter = store.filter().withResetDocumentDateFilter();
                this.setFilter(newFilter);
            },

            /**
             * Sets the create date range filter and returns a new filter instance.
             *
             * @param {IimDateRangeValue} value - The date range value to set
             * @return {void} This method does not return any value.
             */
            setCreateDateRange(value: IimDateRangeValue): void {
                const newFilter = store.filter().withCreateDateRange(value);
                this.setFilter(newFilter);
            },

            /**
             * Resets the create date filter and returns a new filter instance.
             *
             * @return {void} This method does not return any value.
             */
            resetCreateDateFilter(): void {
                const newFilter = store.filter().withResetCreateDateFilter();
                this.setFilter(newFilter);
            },

            /**
             * Sets the general search filter and returns a new filter instance.
             *
             * @param {string | undefined} value - The search value to set
             * @return {void} This method does not return any value.
             */
            setGeneralSearch(value: string | undefined): void {
                const newFilter = store.filter().withGeneralSearch(value);
                this.setFilter(newFilter);
            },

            /**
             * Sets the status filter and returns a new filter instance.
             *
             * @param {ListInvoiceStatus} value - The status value to set
             * @return {void} This method does not return any value.
             */
            setStatus(value: ListInvoiceStatus): void {
                const newFilter = store.filter().withStatus(value);
                this.setFilter(newFilter);
            },
            /**
             * Sets the sorting direction for the store.
             *
             * @param {SortDirection} sortDirection - The desired sorting direction to be applied.
             * @return {void} Does not return a value.
             */
            setSorting(sortDirection: SortDirection): void {
                patchState(store, { sortDirection });
            },
            /**
             * Sets the sorting field for the store.
             *
             * @param {ListInvoiceSorting} sortBy - The field to sort by.
             * @return {void} Does not return a value.
             */
            setSortBy(sortBy: ListInvoiceSorting): void {
                patchState(store, { sortBy });
            },
            /**
             * Sets the pagination settings for the list of invoices.
             *
             * @param {ListInvoicePagination} pagination - The pagination configuration to update the state with.
             * @return {void} This method does not return a value.
             */
            setPagination(pagination: ListInvoicePagination): void {
                patchState(store, { pagination });
            },
            /**
             * Initializes the invoice list module by setting up pagination, sorting, and filters based on the provided
             * query params the list is called with.
             *
             * @param {Params} params - The route parameters used to configure pagination, sorting, and filtering.
             * @return {Promise<void>} A promise that resolves when the initialization process is complete.
             */
            async init(params: Params): Promise<void> {
                // set pagination
                const userPagination: UserPagination = await paginationService.createDefaultPagination();
                let pagination = ListInvoicePagination.fromRouteParams(params);
                if (!params[ListInvoicePagination.ELEMENTS_PER_PAGE]) {
                    pagination = pagination.withElementsPerPage(userPagination.elementsPerPage);
                }
                patchState(store, { pagination });

                // set sorting
                if (params[INVOICE_SORT_DIRECTION]) {
                    patchState(store, { sortDirection: params[INVOICE_SORT_DIRECTION] });
                }

                // now set filters
                if (params['clean']) {
                    this.setFilter(ListInvoiceFilter.EMPTY());
                } else if (ListInvoiceFilter.isFilterParam(params)) {
                    const restoredFilter = ListInvoiceFilter.fromRouteParams(params);
                    this.setFilter(restoredFilter);
                } else if (await storageService.has(StorageKey.INVOICE_LIST_FILTER)) {
                    const filterDto = await storageService.getItemObject(StorageKey.INVOICE_LIST_FILTER);
                    const restoredFilter = ListInvoiceFilter.fromDto(filterDto);
                    this.setFilter(restoredFilter);
                }
            },
            /**
             * Loads data by applying the current filter, pagination, and sorting direction from the store.
             * Executes a series of asynchronous operations to fetch the data from the `dataService.getListNew()`.
             * The `isLoading` state is updated during the process to reflect the loading status.
             * On successful data retrieval, the state is updated with the received list of invoices,
             * along with the updated pagination information.
             * If an error occurs during data retrieval, the `isLoading` state is reset.
             */
            loadData: rxMethod<void>(
                pipe(
                    tap(() => patchState(store, { isLoading: true })),
                    switchMap(() =>
                        dataService.getListNew(store.filter(), store.pagination(), store.sortDirection(), store.sortBy()).pipe(
                            tap({
                                next: (response) =>
                                    patchState(store, {
                                        invoices: response.data.map((dto) => new ListInvoice(dto)),
                                        pagination: store.pagination().apply({
                                            ...response.pagination,
                                            page: response.pagination.page + 1,
                                        }),
                                        isLoading: false,
                                    }),
                                error: () => {
                                    patchState(store, { isLoading: false });
                                },
                            }),
                        ),
                    ),
                ),
            ),
            /**
             * A reactive method to lazy load the invoice preview data.
             * This method retrieves the preview data for a given invoice
             * by making an API call and applies the data to the invoice
             * instance using the `applyPreviewData` method.
             *
             * The process involves:
             * - Listening for the provided invoice as input.
             * - Fetching the invoice detail using the `dataService.getStandaloneInvoice` method.
             * - Converting the InvoiceDetail to a format compatible with IListInvoicePreviewDto.
             * - Putting all items in a single group using InvoiceItemGroup, as we no longer maintain
             *   order and delivery note connections.
             * - Applying the converted preview data to the invoice object.
             *
             * @property {rxMethod<ListInvoice>} lazyLoadPreview - The reactive method for handling invoice preview loading.
             */
            lazyLoadPreview: rxMethod<ListInvoice>(
                pipe(
                    switchMap((invoice: ListInvoice) =>
                        dataService.getStandaloneInvoicePreview(invoice.id.toString()).pipe(
                            tap({
                                next: (preview: IStandaloneInvoicePreviewDto) => {
                                    const updatedInvoice = invoice.applyPreviewData(preview);
                                    const invoiceList = store.invoices().slice();
                                    const index = invoiceList.indexOf(invoice);
                                    invoiceList[index] = updatedInvoice;
                                    patchState(store, {
                                        invoices: invoiceList.slice(),
                                    });
                                },
                            }),
                        ),
                    ),
                ),
            ),
        }),
    ),
);
